import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../http';
import { Link } from 'react-router-dom';
import SkillChecklistModal from './SkillChecklistModal';
import ConfirmDialog from '../../components/ConfirmDialog';

const SkillsManager = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChapter, setFilterChapter] = useState('');
  const [filterLevel, setFilterLevel] = useState('');

  useEffect(() => {
    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const fetchSkills = async () => {
    try {
      const params = showArchived ? '?include_archived=true' : '';
      const res = await axios.get(`/api/admin/skills${params}`);
      setSkills(res.data);
    } catch (err) {
      console.error('Failed to load skills', err);
    } finally {
      setLoading(false);
    }
  };

  // Derive available chapters from loaded data
  const availableChapters = useMemo(() => {
    const chapters = [...new Set(skills.map((s) => s.chapter).filter(Boolean))].sort(
      (a, b) => a - b
    );
    return chapters;
  }, [skills]);

  // Client-side filtering for instant responsiveness
  const filteredSkills = useMemo(() => {
    return skills.filter((skill) => {
      const matchesSearch =
        !searchTerm || skill.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesChapter = !filterChapter || skill.chapter === parseInt(filterChapter, 10);
      const matchesLevel = !filterLevel || skill.level === filterLevel;
      return matchesSearch && matchesChapter && matchesLevel;
    });
  }, [skills, searchTerm, filterChapter, filterLevel]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;

    try {
      const res = await axios.post('/api/admin/skills', {
        name: newSkillName,
        pass_criteria: 'all_pass',
        pass_threshold: null
      });
      setSkills([...skills, { ...res.data, name: newSkillName }]);
      setNewSkillName('');
      setShowModal(false);
      fetchSkills();
    } catch (err) {
      alert('Failed to create skill');
    }
  };

  const handleDeactivate = (skill) => {
    setConfirm({
      message: `Deactivate "${skill.name}"? This skill will no longer appear in the skills library or template builder, but all historical test records referencing it will be preserved.`,
      confirmLabel: 'Deactivate',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.patch(`/api/admin/skills/${skill.id}/deactivate`);
          fetchSkills();
        } catch (err) {
          alert('Failed to deactivate skill');
        }
      }
    });
  };

  const handleReactivate = async (skill) => {
    try {
      await axios.patch(`/api/admin/skills/${skill.id}/reactivate`);
      fetchSkills();
    } catch (err) {
      alert('Failed to reactivate skill');
    }
  };

  const chipStyle = (active) => ({
    padding: '0.4rem 0.85rem',
    borderRadius: '20px',
    border: active ? '2px solid #3182ce' : '1px solid #cbd5e0',
    background: active ? '#ebf4ff' : '#fff',
    color: active ? '#2b6cb0' : '#4a5568',
    cursor: 'pointer',
    fontWeight: active ? '600' : '400',
    fontSize: '0.85rem',
    transition: 'all 0.15s ease'
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}
      >
        <h1>Skills Library</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setShowArchived((v) => !v)}
            style={showArchived ? { background: '#fef3c7', color: '#92400e', border: '1px solid #f6d860' } : {}}
          >
            {showArchived ? '📁 Showing Archived' : '📁 Show Archived'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Skill
          </button>
          <Link to="/admin/dashboard" className="btn btn-secondary">
            Back to Admin
          </Link>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div
        style={{
          background: '#f7fafc',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
          border: '1px solid #e2e8f0'
        }}
      >
        <input
          type="text"
          className="form-input"
          placeholder="Search skills..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '1 1 200px', minWidth: '180px', maxWidth: '350px' }}
        />

        <select
          className="form-input"
          value={filterChapter}
          onChange={(e) => setFilterChapter(e.target.value)}
          style={{ minWidth: '140px', maxWidth: '180px' }}
        >
          <option value="">All Chapters</option>
          {availableChapters.map((ch) => (
            <option key={ch} value={ch}>
              Chapter {ch}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            style={chipStyle(filterLevel === '')}
            onClick={() => setFilterLevel('')}
          >
            All Levels
          </button>
          <button
            type="button"
            style={chipStyle(filterLevel === 'FF1')}
            onClick={() => setFilterLevel(filterLevel === 'FF1' ? '' : 'FF1')}
          >
            🔶 FF1
          </button>
          <button
            type="button"
            style={chipStyle(filterLevel === 'FF2')}
            onClick={() => setFilterLevel(filterLevel === 'FF2' ? '' : 'FF2')}
          >
            🔷 FF2
          </button>
        </div>

        {(searchTerm || filterChapter || filterLevel) && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setSearchTerm('');
              setFilterChapter('');
              setFilterLevel('');
            }}
            style={{ background: '#fed7d7', color: '#c53030', border: 'none', marginLeft: 'auto' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results count */}
      {!loading && (
        <div style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: '#718096' }}>
          Showing {filteredSkills.length} of {skills.length} skills
          {showArchived && ' (including archived)'}
        </div>
      )}

      {/* Add Skill Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="wizard-modal">
            <h3>Add New Skill</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Skill Name</label>
                <input
                  className="form-input"
                  placeholder="e.g. Ladder Climb"
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Skill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <thead style={{ background: '#f7fafc' }}>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #edf2f7' }}>
              <th style={{ padding: '1rem' }}>Name</th>
              <th style={{ padding: '1rem', width: '80px' }}>Chapter</th>
              <th style={{ padding: '1rem', width: '65px' }}>Level</th>
              {showArchived && <th style={{ padding: '1rem', width: '80px' }}>Status</th>}
              <th style={{ padding: '1rem', width: '160px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSkills.map((skill) => (
              <tr
                key={skill.id}
                style={{
                  borderBottom: '1px solid #edf2f7',
                  opacity: skill.is_active ? 1 : 0.55,
                  background: skill.is_active ? 'white' : '#fafafa'
                }}
              >
                <td style={{ padding: '1rem' }}>{skill.name}</td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>{skill.chapter || '—'}</td>
                <td style={{ padding: '1rem' }}>
                  {skill.level === 'FF1' && (
                    <span style={{ color: '#dd6b20', fontWeight: '600', fontSize: '0.85rem' }}>
                      FF1
                    </span>
                  )}
                  {skill.level === 'FF2' && (
                    <span style={{ color: '#3182ce', fontWeight: '600', fontSize: '0.85rem' }}>
                      FF2
                    </span>
                  )}
                  {!skill.level && '—'}
                </td>
                {showArchived && (
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: skill.is_active ? '#276749' : '#744210',
                      background: skill.is_active ? '#c6f6d5' : '#fefcbf',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      {skill.is_active ? 'Active' : 'Archived'}
                    </span>
                  </td>
                )}
                <td style={{ padding: '1rem', display: 'flex', gap: '0.4rem' }}>
                  {skill.is_active && (
                    <button
                      onClick={() => setSelectedSkill(skill)}
                      className="btn btn-sm btn-secondary"
                    >
                      Edit Steps
                    </button>
                  )}
                  {skill.is_active ? (
                    <button
                      onClick={() => handleDeactivate(skill)}
                      className="btn btn-sm"
                      style={{ background: '#fed7d7', color: '#c53030' }}
                    >
                      Retire
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivate(skill)}
                      className="btn btn-sm"
                      style={{ background: '#c6f6d5', color: '#276749' }}
                    >
                      Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Edit Steps Modal */}
      {selectedSkill && (
        <SkillChecklistModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />
      )}
    </div>
  );
};

export default SkillsManager;
